import { v4 as uuidv4 } from 'uuid';
import {
  PresignedUploadRequest,
  PresignedUploadResponse,
} from '../media.types';

/** Where the mocked bucket lives. Written to, never read from — as in production. */
export const MOCK_BUCKET_HOST = 'https://mock.s3.us-east-1.amazonaws.com';

/**
 * Where the mocked distribution lives.
 *
 * The same host the stored fixtures use, so one handler serves an asset uploaded
 * in this session and one that was already in the config.
 */
export const MOCK_CDN_HOST = 'https://media.incentivio.com';

/** The client the fixtures belong to, when no header says otherwise. */
export const FIXTURE_CLIENT_ID = '1b63163d-1aba-4944-bbc1-ebc495d9dc5c';

const FOLDER_BY_OBJECT_TYPE: Record<string, string> = {
  APP_CONFIG: 'ac',
};

const stemOf = (name: string) => name.replace(/\.[^.]+$/, '');
const extensionOf = (name: string) => {
  const at = name.lastIndexOf('.');
  return at > 0 ? name.slice(at) : '';
};

/**
 * The name the service assigns: the caller's stem, then a uuid with its dashes
 * stripped. Mirrored rather than simplified, because the uniqueness is what makes
 * a replaced asset a new url — so diffs and cache-busting behave here the way
 * they will in production.
 */
export const uniqueFileName = (fileName: string) =>
  `${stemOf(fileName)}_${uuidv4().replace(/-/g, '')}${extensionOf(fileName)}`;

export const objectKeyFor = (
  clientId: string,
  objectType: string,
  fileName: string,
) =>
  `${clientId}/${
    FOLDER_BY_OBJECT_TYPE[objectType] ?? objectType.toLowerCase()
  }/${uniqueFileName(fileName)}`;

/**
 * Both urls for one upload, from one key.
 *
 * The write host and the read host are deliberately different, because they are
 * different in production — a signed PUT goes to the bucket and every read goes
 * through the distribution. A mock that collapsed them into one host would let
 * code that reads from the bucket look correct.
 */
export const createPresignedUpload = (
  request: PresignedUploadRequest,
  clientId?: string | null,
): PresignedUploadResponse => {
  const key = objectKeyFor(
    clientId || FIXTURE_CLIENT_ID,
    request.objectType,
    request.fileName,
  );

  return {
    key,
    presignedUrl: `${MOCK_BUCKET_HOST}/${key}?sig=mock`,
    downloadUrl: `${MOCK_CDN_HOST}/${key}`,
  };
};
